import React, { useState } from "react";
import PageHeader from "../../components/common/PageHeader";
import StatsCardGroup from "../../components/common/StatsCardGroup";
import BarAnalyticsChart from "../../components/vendors/BarAnalyticsChart";
import {
  CurrencyDollarIcon,
  CalendarIcon,
  StarIcon,
  UserGroupIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import BookingsTrendChart from "../../components/vendors/BookingTrendChart";
import Table from "../../components/common/Table";
import SearchBar from "../../components/common/Search";
import VendorCarouselCards from "../../components/vendors/VendorCarouselCards";
import StatusBadge from "../../components/common/StatusBadge";
import Pagination from "../../components/common/Pagination";
import {
  useVendorDashboardOverview,
  useVendorEarningsTrend,
  useVendorAnalyticsBookingTrend,
  useVendorTopExperiences,
  useVendorRecentReviews,
} from "../../custom-hooks/queries";
import { formatValue, trimFloatNumber } from "../../utils/HelperMethod";

const Analytics = () => {
  const currentYear = new Date().getFullYear();

  // State for earnings chart filter
  const [earningsRange, setEarningsRange] = useState("year");
  const [earningsYear, setEarningsYear] = useState(currentYear);

  // State for top experiences table
  const [experiencesPage, setExperiencesPage] = useState(1);
  const [experiencesSearch, setExperiencesSearch] = useState("");
  const experiencesPageSize = 6;

  // State for reviews
  const [reviewsPage, setReviewsPage] = useState(1);
  const reviewsPageSize = 10;

  // Fetch dashboard overview for stats
  const { data: overviewData, isLoading: isOverviewLoading } =
    useVendorDashboardOverview();

  // Fetch earnings trend
  const { data: earningsData, isLoading: isEarningsLoading } =
    useVendorEarningsTrend({
      range: earningsRange,
      year: earningsYear,
    });

  // Fetch booking trend for chart
  const { data: bookingTrendData, isLoading: isBookingTrendLoading } =
    useVendorAnalyticsBookingTrend();

  // Fetch top experiences
  const { data: topExperiencesData, isLoading: isTopExperiencesLoading } =
    useVendorTopExperiences({
      page: experiencesPage,
      pageSize: experiencesPageSize,
      search: experiencesSearch,
    });

  // Fetch recent reviews
  const { data: reviewsData, isLoading: isReviewsLoading } =
    useVendorRecentReviews({
      page: reviewsPage,
      pageSize: reviewsPageSize,
    });

  // Format number for display

  // Stats data from API
  // const dashboardCards = overviewData?.dashboardCards || {};

  const stats = [
    {
      icon: <CurrencyDollarIcon className="h-6 w-6" />,
      label: "Total Earning",
      value: formatValue(
        overviewData?.data?.dashboardCards?.totalEarnings,
        "currency",
      ),
    },
    {
      icon: <CalendarIcon className="h-6 w-6" />,
      label: "Total Bookings",
      value: formatValue(
        overviewData?.data?.dashboardCards?.totalBookings,
        "count",
      ),
    },
    {
      icon: <StarIcon className="h-6 w-6" />,
      label: "Average Rating",
      value:
        trimFloatNumber(overviewData?.data?.dashboardCards?.averageRating) ||
        "-",
    },
    {
      icon: <UserGroupIcon className="h-6 w-6" />,
      label: "Total Experiences",
      value: formatValue(
        overviewData?.data?.dashboardCards?.totalExperiences,
        "count",
      ),
    },
    {
      icon: <ClockIcon className="h-6 w-6" />,
      label: "Upcoming Bookings",
      value: formatValue(
        overviewData?.data?.dashboardCards?.upcomingBookings,
        "count",
      ),
    },
  ];

  // Map earnings trend data for chart
  const earningsChartData =
    earningsData?.data?.points?.map((item) => ({
      month: item.label,
      value: item.earnings,
    })) || [];

  // Map booking trend data for horizontal bar chart
  const bookingTrendChartData =
    bookingTrendData?.data?.topExperiences?.map((item) => ({
      name: item.title,
      value: item.bookings,
    })) || [];

  // Map top experiences for table
  const topExperiences =
    topExperiencesData?.data?.data?.map((item) => ({
      experienceId: item.experienceId,
      experience: item.title,
      bookings: item.totalBookings,
      earnings: item.totalEarnings,
      rating: item.rating || "-",
      status: item.status || "Active",
    })) || [];

  const experiencesPagination = {
    currentPage: topExperiencesData?.data?.currentPage,
    pageSize: topExperiencesData?.data?.pageSize,
    totalCount: topExperiencesData?.data?.totalCount,
    totalPages: topExperiencesData?.data?.totalPages,
  };

  // Map reviews for carousel
  const reviews =
    reviewsData?.data?.data?.map((item) => ({
      reviewId: item.reviewId,
      name: item.user?.name,
      avatar: item.user?.profileImageUrl,
      experience: item.experience?.title,
      rating: item.rating,
      text: item.comment,
      time: new Date(item.createdAt).toLocaleDateString(),
    })) || [];

  const experienceColumns = [
    {
      key: "experience",
      label: "EXPERIENCE",
      width: "1.6fr",
      align: "left",
    },
    {
      key: "bookings",
      label: "BOOKINGS",
      width: "1fr",
      align: "center",
      render: (row) => row.bookings,
    },
    {
      key: "earnings",
      label: "EARNINGS",
      width: "1fr",
      align: "center",
      render: (row) => `$${row.earnings}`,
    },
    {
      key: "rating",
      label: "RATING",
      width: "1fr",
      align: "center",
      render: (row) => row.rating,
    },
    {
      key: "status",
      label: "STATUS",
      width: "1fr",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  // Handler for earnings chart filter
  const handleEarningsFilterChange = (filter) => {
    if (filter === "Last 3 Months") {
      setEarningsRange("last3months");
    } else if (filter === "Last 6 Months") {
      setEarningsRange("last6months");
    }
  };

  const handleEarningsYearChange = (year) => {
    setEarningsRange("year");
    setEarningsYear(year);
  };

  // Handler for experiences search
  const handleExperiencesSearch = (value) => {
    setExperiencesSearch(value);
    setExperiencesPage(1);
  };

  // Handler for experiences pagination
  const handleExperiencesPageChange = (page) => {
    setExperiencesPage(page);
  };

  return (
    <div className="px-4 py-1 sm:px-6 sm:py-1">
      <PageHeader title="Analytics" />

      <div className="mt-4 sm:mt-6">
        <StatsCardGroup stats={stats} isLoading={isOverviewLoading} />
      </div>

      <div className="mt-4 sm:mt-6">
        <BarAnalyticsChart
          data={earningsChartData}
          showToggle={true}
          showFilters={true}
          showYearDropdown={true}
          heading="Earnings"
          onYearChange={handleEarningsYearChange}
          selectedYear={earningsYear}
          onFilterChange={handleEarningsFilterChange}
          isLoading={isEarningsLoading}
          earningsRange={earningsRange}
        />
      </div>

      <div className="mt-4 sm:mt-5 bg-white border border-[#E5E7EB] rounded-2xl p-4 sm:p-6 shadow-sm">
        <BookingsTrendChart
          data={bookingTrendChartData}
          isLoading={isBookingTrendLoading}
        />
      </div>

      <div
        className="
          bg-white 
          rounded-[12px]
          border border-[#D9D9D9]
          shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]
          overflow-hidden 
          mt-4 sm:mt-5"
      >
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 sm:gap-0 py-4 sm:py-6 px-4 sm:px-6">
          <PageHeader title="Top Experiences" margin={false} />
          <SearchBar onSearch={handleExperiencesSearch} />
        </div>

        <Table
          columns={experienceColumns}
          data={topExperiences}
          isLoading={isTopExperiencesLoading}
          skeletonRows={experiencesPageSize}
        />

        {!isTopExperiencesLoading && experiencesPagination.totalPages > 1 && (
          <div className="px-4 sm:px-6 py-4">
            <Pagination
              currentPage={experiencesPagination.currentPage || experiencesPage}
              totalPages={experiencesPagination.totalPages || 1}
              onPageChange={handleExperiencesPageChange}
            />
          </div>
        )}
      </div>

      {/* Only show reviews section if there are reviews */}
      {(isReviewsLoading || reviews.length > 0) && (
        <div className="mt-4 sm:mt-5">
          <VendorCarouselCards reviews={reviews} isLoading={isReviewsLoading} />
        </div>
      )}
    </div>
  );
};
export default Analytics;
